import { useState, useEffect, useMemo } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Briefcase, Search, CheckCircle2, XCircle, Clock, Users2, ChevronRight, Paperclip, Star, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'applied', label: 'Applied', color: 'bg-neutral-100 text-neutral-700 border-neutral-200', icon: Clock },
  { key: 'shortlisted', label: 'Shortlisted', color: 'bg-blue-50 text-blue-700 border-blue-100', icon: CheckCircle2 },
  { key: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-indigo-50 text-indigo-700 border-indigo-100', icon: Clock },
  { key: 'interview_completed', label: 'Interview Completed', color: 'bg-purple-50 text-purple-700 border-purple-100', icon: CheckCircle2 },
  { key: 'selected', label: 'Selected', color: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: CheckCircle2 },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-100', icon: XCircle },
];

const getStage = (s) => STAGES.find(x => x.key === s) || STAGES[0];
const nextStageOf = (s) => {
  if (s === 'selected' || s === 'rejected') return null;
  const idx = STAGES.findIndex(x => x.key === s);
  return STAGES[idx + 1]?.key;
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function RecruitmentPage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'management'].includes(user?.system_role);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', position: '', branch_id: '', division: '', source: '', notes: '' });
  const [docs, setDocs] = useState([]); // {name, type, data}

  const [detailDialog, setDetailDialog] = useState({ open: false, candidate: null });
  const [feedbackForm, setFeedbackForm] = useState({ interviewer: '', rating: 4, remarks: '' });
  const [promoteDialog, setPromoteDialog] = useState({ open: false, candidate: null, credentials: null });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterBranch !== 'all') params.branch_id = filterBranch;
      if (search) params.search = search;
      const [listRes, statsRes, bRes] = await Promise.all([
        api.get('/candidates', { params }),
        api.get('/candidates/stats'),
        api.get('/branches'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
      setBranches(bRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filterStatus, filterBranch]);

  const branchName = (id) => branches.find(b => b.id === id)?.name || '-';

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.phone || !form.position) {
      toast.error('Name, Email, Phone, Position are required');
      return;
    }
    try {
      const payload = { ...form, documents: docs };
      if (!payload.branch_id) delete payload.branch_id;
      await api.post('/candidates', payload);
      toast.success('Candidate added');
      setCreateOpen(false);
      setForm({ name: '', email: '', phone: '', position: '', branch_id: '', division: '', source: '', notes: '' });
      setDocs([]);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/candidates/${id}`, { status: newStatus });
      toast.success(`Moved to ${getStage(newStatus).label}`);
      fetchData();
      if (detailDialog.open && detailDialog.candidate?.id === id) {
        const res = await api.get(`/candidates/${id}`);
        setDetailDialog({ open: true, candidate: res.data });
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openDetail = async (c) => {
    try {
      const res = await api.get(`/candidates/${c.id}`);
      setDetailDialog({ open: true, candidate: res.data });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleAddFeedback = async () => {
    if (!feedbackForm.interviewer) { toast.error('Interviewer name required'); return; }
    try {
      const res = await api.post(`/candidates/${detailDialog.candidate.id}/add-feedback`, feedbackForm);
      setDetailDialog({ open: true, candidate: res.data });
      setFeedbackForm({ interviewer: '', rating: 4, remarks: '' });
      toast.success('Feedback added');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handlePromote = async () => {
    try {
      const res = await api.post(`/candidates/${promoteDialog.candidate.id}/promote`, {});
      setPromoteDialog(p => ({ ...p, credentials: res.data }));
      toast.success(`Promoted to employee ${res.data.employee.employee_code}`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this candidate permanently?')) return;
    try {
      await api.delete(`/candidates/${id}`);
      toast.success('Candidate deleted');
      fetchData();
      setDetailDialog({ open: false, candidate: null });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDocUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be < 5MB'); return; }
    const b64 = await fileToBase64(file);
    setDocs(d => [...d, { name: file.name, type, data: b64 }]);
    e.target.value = '';
  };

  const copyToClipboard = (t) => {
    navigator.clipboard.writeText(t);
    toast.success('Copied');
  };

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      i.name?.toLowerCase().includes(q) || i.email?.toLowerCase().includes(q) || i.phone?.includes(q) || i.position?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const positions = useMemo(() => [...new Set(items.map(i => i.position).filter(Boolean))], [items]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recruitment-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Recruitment</h1>
          <p className="text-neutral-700 text-sm mt-1">Paperless candidate pipeline → auto-promoted to Employee Data Bank</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="add-candidate-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Add Candidate
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recruitment-stats">
        {[
          { label: 'Total Candidates', value: stats.total || 0, icon: Users2, color: 'bg-neutral-50 text-neutral-950 border-neutral-200' },
          { label: 'Pending Interviews', value: stats.pending_interviews || 0, icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-100' },
          { label: 'Selected', value: stats.selected || 0, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
          { label: 'Rejected', value: stats.rejected || 0, icon: XCircle, color: 'bg-red-50 text-red-700 border-red-100' },
        ].map((s) => (
          <div key={s.label} className={`border rounded-md p-4 ${s.color}`}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{s.label}</p>
              <s.icon className="h-4 w-4 opacity-60" />
            </div>
            <p className="text-3xl font-bold tracking-tight mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card className="border border-neutral-200 rounded-md">
        <CardContent className="p-3 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Search name, email, phone, position..." value={search}
              onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="recruit-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="recruit-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="recruit-filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Candidate</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Contact</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Position</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Branch</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">Stage</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-700 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={6}><div className="h-10 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>)
              : filteredItems.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-neutral-700">
                  <Briefcase className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                  <p className="text-sm font-medium">No candidates yet</p>
                  <p className="text-xs mt-1 text-neutral-600">Click <strong>Add Candidate</strong> to start the pipeline.</p>
                </TableCell></TableRow>
              ) : filteredItems.map(c => {
                const stage = getStage(c.status);
                const next = nextStageOf(c.status);
                return (
                  <TableRow key={c.id} className="hover:bg-neutral-50" data-testid={`candidate-row-${c.id}`}>
                    <TableCell>
                      <p className="text-sm font-semibold text-neutral-950">{c.name}</p>
                      {c.promoted_to_employee_id && <p className="text-[10px] text-emerald-700 font-medium">✓ In Employee Data Bank</p>}
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-neutral-800">{c.email}</p>
                      <p className="text-[11px] text-neutral-600">{c.phone}</p>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-800">{c.position}</TableCell>
                    <TableCell className="text-xs text-neutral-700">{branchName(c.branch_id)}</TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${stage.color} font-semibold`}>
                        <stage.icon className="h-3 w-3 mr-1" /> {stage.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {next && !c.promoted_to_employee_id && (
                          <Button variant="ghost" size="sm" className="h-7 text-[11px] text-blue-700 hover:bg-blue-50"
                            onClick={() => handleStatusChange(c.id, next)} title={`Move to ${getStage(next).label}`}
                            data-testid={`advance-${c.id}`}>
                            Advance <ChevronRight className="h-3 w-3 ml-0.5" />
                          </Button>
                        )}
                        {c.status !== 'rejected' && !c.promoted_to_employee_id && (
                          <Button variant="ghost" size="sm" className="h-7 text-[11px] text-red-600 hover:bg-red-50"
                            onClick={() => handleStatusChange(c.id, 'rejected')} data-testid={`reject-${c.id}`}>
                            Reject
                          </Button>
                        )}
                        {c.status === 'selected' && !c.promoted_to_employee_id && isAdmin && (
                          <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => setPromoteDialog({ open: true, candidate: c, credentials: null })}
                            data-testid={`promote-${c.id}`}>
                            Promote → Employee
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => openDetail(c)} data-testid={`view-${c.id}`}>View</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* ── Create Candidate Dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl" data-testid="create-candidate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">New Candidate</DialogTitle>
            <DialogDescription>Enter candidate details. Status starts at "Applied".</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2 max-h-[70vh] overflow-y-auto">
            <div className="space-y-1"><Label className="text-xs font-medium">Full Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-sm" data-testid="candidate-name-input" /></div>
            <div className="space-y-1"><Label className="text-xs font-medium">Position Applied *</Label><Input value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))} className="rounded-sm" data-testid="candidate-position-input" placeholder="Sales Girl / Cashier / Manager" /></div>
            <div className="space-y-1"><Label className="text-xs font-medium">Email *</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="rounded-sm" data-testid="candidate-email-input" /></div>
            <div className="space-y-1"><Label className="text-xs font-medium">Phone *</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="rounded-sm" data-testid="candidate-phone-input" /></div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Branch</Label>
              <Select value={form.branch_id || ''} onValueChange={v => setForm(p => ({ ...p, branch_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="candidate-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs font-medium">Division</Label><Input value={form.division} onChange={e => setForm(p => ({ ...p, division: e.target.value }))} className="rounded-sm" placeholder="Retail / Textile" /></div>
            <div className="space-y-1 col-span-2"><Label className="text-xs font-medium">Source</Label><Input value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className="rounded-sm" placeholder="Walk-in / Referral / Job portal" /></div>
            <div className="space-y-1 col-span-2"><Label className="text-xs font-medium">Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="rounded-sm" /></div>
            <div className="col-span-2 space-y-2 border-t border-neutral-200 pt-3">
              <Label className="text-xs font-medium text-neutral-950">Documents</Label>
              <div className="grid grid-cols-2 gap-2">
                {[['resume', 'Resume'], ['id_proof', 'ID Proof'], ['certificate', 'Certificate'], ['other', 'Other']].map(([t, lbl]) => (
                  <label key={t} className="flex items-center gap-2 p-2 border border-dashed border-neutral-300 rounded-sm cursor-pointer hover:border-red-400">
                    <Paperclip className="h-3.5 w-3.5 text-neutral-600" />
                    <span className="text-xs text-neutral-800">{lbl}</span>
                    <input type="file" className="hidden" onChange={e => handleDocUpload(e, t)} accept=".pdf,.jpg,.jpeg,.png" />
                  </label>
                ))}
              </div>
              {docs.length > 0 && (
                <div className="space-y-1">
                  {docs.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-neutral-50 px-2 py-1 rounded-sm">
                      <span className="truncate">{d.name} <span className="text-neutral-600">· {d.type}</span></span>
                      <button onClick={() => setDocs(ds => ds.filter((_, j) => j !== i))} className="text-red-600 hover:underline">remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="save-candidate-btn">Add Candidate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Candidate Detail Dialog ── */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => setDetailDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-3xl" data-testid="candidate-detail-dialog">
          {detailDialog.candidate && (() => {
            const c = detailDialog.candidate;
            const stage = getStage(c.status);
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <DialogTitle className="font-heading font-bold text-xl">{c.name}</DialogTitle>
                      <DialogDescription>{c.position} · {branchName(c.branch_id)} · applied {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</DialogDescription>
                    </div>
                    <Badge className={`${stage.color} text-xs font-semibold`}><stage.icon className="h-3 w-3 mr-1" />{stage.label}</Badge>
                  </div>
                </DialogHeader>
                <div className="py-2 space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* Stage pipeline */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {STAGES.filter(s => s.key !== 'rejected').map((s, i) => {
                      const currentIdx = STAGES.findIndex(x => x.key === c.status);
                      const thisIdx = STAGES.findIndex(x => x.key === s.key);
                      const done = currentIdx >= thisIdx && c.status !== 'rejected';
                      return (
                        <div key={s.key} className={`flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-medium ${done ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-100 text-neutral-600'}`}>
                          {done ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {s.label}
                          {i < STAGES.length - 2 && <ChevronRight className="h-3 w-3 ml-1 text-neutral-400" />}
                        </div>
                      );
                    })}
                    {c.status === 'rejected' && <Badge className="bg-red-50 text-red-700 border-red-100 text-xs">REJECTED</Badge>}
                  </div>
                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-neutral-50 rounded-md">
                    <div><p className="text-[10px] uppercase text-neutral-600 font-bold">Email</p><p className="text-sm text-neutral-950">{c.email}</p></div>
                    <div><p className="text-[10px] uppercase text-neutral-600 font-bold">Phone</p><p className="text-sm text-neutral-950">{c.phone}</p></div>
                    <div><p className="text-[10px] uppercase text-neutral-600 font-bold">Division</p><p className="text-sm text-neutral-950">{c.division || '—'}</p></div>
                    <div><p className="text-[10px] uppercase text-neutral-600 font-bold">Source</p><p className="text-sm text-neutral-950">{c.source || '—'}</p></div>
                    {c.notes && <div className="col-span-2"><p className="text-[10px] uppercase text-neutral-600 font-bold">Notes</p><p className="text-sm text-neutral-800">{c.notes}</p></div>}
                  </div>
                  {/* Documents */}
                  <div>
                    <p className="text-xs font-bold uppercase text-neutral-700 mb-2">Documents ({c.documents?.length || 0})</p>
                    {(c.documents || []).length === 0 ? <p className="text-xs text-neutral-600">No documents uploaded.</p> : (
                      <div className="grid grid-cols-2 gap-2">
                        {c.documents.map((d, i) => (
                          <a key={`${d.name}-${i}`} href={`data:application/octet-stream;base64,${d.data}`} download={d.name}
                             className="flex items-center gap-2 p-2 border border-neutral-200 rounded-sm hover:border-red-400 hover:bg-red-50/50">
                            <Paperclip className="h-3.5 w-3.5 text-neutral-700" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate text-neutral-900">{d.name}</p>
                              <p className="text-[10px] text-neutral-600 capitalize">{d.type}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Interview Feedback */}
                  <div>
                    <p className="text-xs font-bold uppercase text-neutral-700 mb-2">Interview Feedback</p>
                    {(c.interview_feedback || []).length > 0 ? (
                      <div className="space-y-2 mb-3">
                        {c.interview_feedback.map((f, i) => (
                          <div key={`${f.date}-${f.interviewer}-${i}`} className="border border-neutral-200 rounded-sm p-3 bg-white">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-neutral-900">{f.interviewer}</p>
                              <div className="flex gap-0.5">
                                {[1,2,3,4,5].map(n => <Star key={n} className={`h-3.5 w-3.5 ${n <= (f.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'}`} />)}
                              </div>
                            </div>
                            {f.remarks && <p className="text-xs text-neutral-800 mt-1">{f.remarks}</p>}
                            <p className="text-[10px] text-neutral-500 mt-1">{new Date(f.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-neutral-600 mb-3">No feedback yet.</p>}
                    {c.status !== 'rejected' && !c.promoted_to_employee_id && (
                      <div className="grid grid-cols-2 gap-2 p-3 bg-neutral-50 rounded-md">
                        <Input placeholder="Interviewer name" value={feedbackForm.interviewer}
                          onChange={e => setFeedbackForm(p => ({ ...p, interviewer: e.target.value }))} className="rounded-sm" data-testid="fb-interviewer" />
                        <Select value={String(feedbackForm.rating)} onValueChange={v => setFeedbackForm(p => ({ ...p, rating: parseInt(v) }))}>
                          <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} / 5 stars</SelectItem>)}</SelectContent>
                        </Select>
                        <Textarea placeholder="Remarks..." rows={2} value={feedbackForm.remarks}
                          onChange={e => setFeedbackForm(p => ({ ...p, remarks: e.target.value }))} className="col-span-2 rounded-sm" data-testid="fb-remarks" />
                        <Button onClick={handleAddFeedback} className="col-span-2 bg-neutral-900 hover:bg-black text-white rounded-sm" data-testid="fb-add-btn">Add Feedback</Button>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter className="flex items-center justify-between">
                  {isAdmin && !c.promoted_to_employee_id && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                  )}
                  <div className="flex gap-2 ml-auto">
                    {c.status === 'selected' && !c.promoted_to_employee_id && isAdmin && (
                      <Button onClick={() => { setDetailDialog({ open: false, candidate: null }); setPromoteDialog({ open: true, candidate: c, credentials: null }); }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Promote → Employee</Button>
                    )}
                    <Button variant="outline" onClick={() => setDetailDialog({ open: false, candidate: null })} className="rounded-sm">Close</Button>
                  </div>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Promote Dialog ── */}
      <Dialog open={promoteDialog.open} onOpenChange={(o) => !o && setPromoteDialog({ open: false, candidate: null, credentials: null })}>
        <DialogContent className="max-w-md" data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Promote to Employee</DialogTitle>
            <DialogDescription>Creates an active employee profile with login credentials.</DialogDescription>
          </DialogHeader>
          {!promoteDialog.credentials ? (
            <>
              <div className="py-3 space-y-2 text-sm text-neutral-900">
                <p>Candidate <strong>{promoteDialog.candidate?.name}</strong> will be added to the Employee Data Bank.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-neutral-800">
                  <li>Auto-generated Employee Code (KG-series)</li>
                  <li>All documents transferred</li>
                  <li>Temporary password generated — share securely</li>
                  <li>HR completes remaining fields afterwards</li>
                </ul>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPromoteDialog({ open: false, candidate: null, credentials: null })} className="rounded-sm">Cancel</Button>
                <Button onClick={handlePromote} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="confirm-promote-btn">Confirm Promote</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="py-3 space-y-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                  <p className="text-xs font-bold text-emerald-800 uppercase">✓ Promoted Successfully</p>
                  <p className="text-sm text-emerald-900 mt-1">{promoteDialog.credentials.employee.name} · <strong>{promoteDialog.credentials.employee.employee_code}</strong></p>
                </div>
                <div className="space-y-2 p-3 bg-neutral-50 rounded-md border border-neutral-200">
                  <p className="text-[10px] font-bold text-neutral-800 uppercase">Login Credentials (share securely)</p>
                  <div className="flex items-center justify-between gap-2 bg-white p-2 rounded border border-neutral-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-neutral-600">Email</p>
                      <p className="text-xs font-mono text-neutral-950 truncate">{promoteDialog.credentials.employee.email}</p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(promoteDialog.credentials.employee.email)}><Copy className="h-3 w-3" /></Button>
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-white p-2 rounded border border-neutral-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-neutral-600">Temporary Password</p>
                      <p className="text-xs font-mono text-neutral-950 truncate">{promoteDialog.credentials.temp_password}</p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(promoteDialog.credentials.temp_password)}><Copy className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setPromoteDialog({ open: false, candidate: null, credentials: null })} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
